import { Injectable, Inject } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ModalOptions, BsModalRef } from 'ngx-bootstrap/modal';
import { DialogResultType, DialogOptions, EntityList, DialogComment } from '../model';
import { PhxLocalizationService } from './phx-localization.service';
import { PhxDialogConfirmTemplateComponent } from '../components/phx-dialog-confirm-template/phx-dialog-confirm-template.component';
import { PhxDialogWaitTemplateComponent } from '../components/phx-dialog-wait-template/phx-dialog-wait-template.component';
import { PhxDialogErrorTemplateComponent } from '../components/phx-dialog-error-template/phx-dialog-error-template.component';
import { PhxDialogNotifyTemplateComponent } from '../components/phx-dialog-notify-template/phx-dialog-notify-template.component';
import { PhxDialogCommentTemplateComponent } from '../components/phx-dialog-comment-template/phx-dialog-comment-template.component';
import { PhoenixCommonModuleResourceKeys } from '../PhoenixCommonModule.resource-keys';
import { DialogState } from '../model/dialog-state';
import { ApiService } from './api.service';
import { DeclinedReason } from '../model/declined-reason';
import { map } from 'rxjs/operators';

@Injectable()
export class DialogService {
  constructor(
    private locale: PhxLocalizationService, 
    private modalService: BsModalService,
    private apiService: ApiService,) {}

  error(title?: string, message?: string, dialogOptions?: DialogOptions) {
    const modalOption: ModalOptions = this.getModalOption(dialogOptions);
    modalOption.initialState = {
      title: title || this.locale.translate('common.generic.error'),
      message: message || 'An unknown error has occurred.'
    };
    const modalRef: BsModalRef = this.modalService.show(PhxDialogErrorTemplateComponent, modalOption);
    modalRef.content.modalRefHide = () => modalRef.hide();
  }
  wait(title?: string, message?: string, getProgressFn?: () => number, dialogOptions?: DialogOptions) {
    const modalOption: ModalOptions = this.getModalOption(dialogOptions);
    modalOption.initialState = {
      title: title || 'Please Wait...',
      message: message || 'Waiting on operation to complete.',
      getProgressFn: getProgressFn || null
    };
    const modalRef: BsModalRef = this.modalService.show(PhxDialogWaitTemplateComponent, modalOption);
    modalRef.content.modalRefHide = () => modalRef.hide();
  }
  notify(title?: string, message?: string, dialogOptions?: DialogOptions) {
    return new Promise<void>((resolve, reject) => {
      const modalOption: ModalOptions = this.getModalOption(dialogOptions);
      modalOption.initialState = {
        title: title || 'Notification',
        message: message || 'Unknown application notification.',
        callbackFn: () => resolve()
      };
      const modalRef: BsModalRef = this.modalService.show(PhxDialogNotifyTemplateComponent, modalOption);
      modalRef.content.modalRefHide = () => modalRef.hide();
    });
  }
  confirm(title?: string, message?: string, dialogOptions?: DialogOptions, yesButtonText?: string, noButtonText?: string): Promise<DialogResultType> {
    return new Promise((resolve, reject) => {
      const modalOption: ModalOptions = this.getModalOption(dialogOptions);
      // hackiest fix :(
      // https://github.com/valor-software/ngx-bootstrap/issues/5000
      // TODO: add same logic on other modals
      const isNested: boolean = dialogOptions && dialogOptions.isNested;
      if (isNested) {
        document.querySelector('body').classList.add('ngx-bootstrap-fix');
      }
      modalOption.initialState = {
        title: title || 'Confirmation',
        message: message || 'Confirmation required.',
        yesButtonText: yesButtonText,
        noButtonText: noButtonText,
        yesCallbackFn: () => {
          resolve(DialogResultType.Yes);
        },
        noCallbackFn: () => {
          reject(DialogResultType.No);
        }
      };
      const modalRef: BsModalRef = this.modalService.show(PhxDialogConfirmTemplateComponent, modalOption);
      modalRef.content.modalRefHide = () => {
        modalRef.hide();
        if (isNested) {
          document.querySelector('body').classList.remove('ngx-bootstrap-fix');
        }
      };
    });
  }
  confirmDelete(message: string = this.locale.translate('common.dialog.confirmDeleteMessage')) {
    return this.confirm(this.locale.translate('common.dialog.confirmDeleteTitle'), message);
  }
  comment(dialogState: DialogState = {}, dialogOptions?: DialogOptions): Promise<string> {
    return new Promise((resolve, reject) => {
      const modalOption: ModalOptions = this.getModalOption(dialogOptions);
      modalOption.initialState = {
        ...dialogState,
        title: dialogState.title || this.locale.translate(PhoenixCommonModuleResourceKeys.phxDialogComment.title),
        helpblock: dialogState.helpblock || this.locale.translate(PhoenixCommonModuleResourceKeys.phxDialogComment.helpblock),
        maxLength: dialogState.maxLength || 4000,
        saveButtonText: dialogState.saveButtonText || this.locale.translate(PhoenixCommonModuleResourceKeys.phxDialogComment.saveBtn),
        saveCallbackFn: (comment: string) => resolve(comment),
        cancelCallbackFn: () => reject()
      };
      const modalRef: BsModalRef = this.modalService.show(PhxDialogCommentTemplateComponent, modalOption);
      modalRef.content.modalRefHide = () => modalRef.hide();
    });
  }

  commentWithReasons(dialogState: DialogState = {}, dialogOptions?: DialogOptions): Promise<DialogComment> {
    return new Promise(async (resolve, reject) => {
      const modalOption: ModalOptions = this.getModalOption(dialogOptions);
      
      var declineReasonList = await this.getDeclineReasonList(dialogState.entityTypeId);

      modalOption.initialState = {
        ...dialogState,
        title: dialogState.title || this.locale.translate(PhoenixCommonModuleResourceKeys.phxDialogComment.title),
        helpblock: dialogState.helpblock || this.locale.translate(PhoenixCommonModuleResourceKeys.phxDialogComment.helpblock),
        maxLength: dialogState.maxLength || 4000,
        saveButtonText: dialogState.saveButtonText || this.locale.translate(PhoenixCommonModuleResourceKeys.phxDialogComment.saveBtn),
        declineReasonList: declineReasonList,
        showSelectionControls: dialogState.showSelectionControls,
        //saveCallbackFn: (dialogComment: DialogComment) => resolve(dialogComment),
        cancelCallbackFn: () => reject(),
        saveReasonIDsCallbackFn: (dialogComment: DialogComment) => resolve(dialogComment)
      };
      const modalRef: BsModalRef = this.modalService.show(PhxDialogCommentTemplateComponent, modalOption);
      modalRef.content.modalRefHide = () => modalRef.hide();
    });
  }

  async getDeclineReasonList(id: number) {
    try {
      const filter = oreq.filter('EntityTypeId').eq(id);
      const params = oreq
        .request()
        .withFilter(filter)
        .url();
      return this.apiService.query<EntityList<DeclinedReason>>('declinedReason' + '?' + params)
      .pipe(map(x => x.Items)).toPromise();
    } catch (e) {
      return null;
    }
  }

  private getModalOption(dialogOption: DialogOptions = {}) {
    dialogOption = dialogOption || {};
    return <ModalOptions>{
      backdrop: dialogOption.hasOwnProperty('backdrop') ? dialogOption.backdrop : true,
      class: `modal-${dialogOption.size || 'sm'} ${dialogOption.windowClass || ''}` + (dialogOption.classList || []).join(' '),
      animated: dialogOption.animation || false,
      ignoreBackdropClick: true,
      keyboard: false
    };
  }
}